/**
 * Shared constants for the two-variant (full + pinned compact) card detail hero.
 *
 * Both the full hero and the compact bar stay mounted at once. DOM ids that must
 * be unique per instance (the reveal panel, the ••• menu) come from `useId()` at
 * each variant's owner — see the review finding that moved them off module
 * constants. The controls carry plain, unsuffixed `data-testid`s; the two mounted
 * heroes are told apart by their container test ids (`card-detail-hero` and
 * `card-hero-compact`), which a locator scopes through. What stays here is the
 * variant discriminator (a runtime `reveal` concern, not a test id one) and the
 * shared layout metrics.
 */

/** `reveal` disclosure anchor — which of the two mounted heroes owns the panel. */
export type CardHeroVariant = 'full' | 'compact'

export { SENSITIVE_CAPTURE_ATTRIBUTES } from 'src/analytics/userpilotPrivacy'

/**
 * The height of the pinned compact bar, and — because the bar lives in the
 * zero-height gap between the fixed global header and the sticky Subnav — the
 * exact amount the Subnav must stick lower whenever the hero is present. The
 * compact bar's own height and the Subnav offset MUST be this one value so they
 * never drift apart (see the plan §4.4).
 *
 * Confirmed against the design prototype (`paywithextend/extend-design-render`,
 * `prototypes/rcn-card-greenfield/app/globals.css`), where the bar is
 * `.gf-compact-hero.is-open { height: 52px }` with a matching
 * `.gf-compact-inner { height: 52px }`. Corroborated by the prototype's sticky
 * tab bar at `.gf-tabs-bar { top: 116px }` — its 64px global header plus this
 * 52px bar, the same "header height + condensed height" relationship the Subnav
 * `stickyTopOffset` encodes here. Stable across prototype SHAs cafcd46..d66230b.
 *
 * NB 64 is the prototype's *global header* (`.gf-topbar { height: 64px }`), not
 * its compact bar — do not conflate the two.
 */
export const CONDENSED_HERO_HEIGHT = 52

/**
 * The real height of the fixed global header (`TopBar`), which hardcodes
 * `height: 72px`. This is NOT the same as `styleConstants.heights.topNavSMUp`
 * (80px) — that token is what the shared `Subnav` uses for its sticky base, and
 * it over-measures the actual header by 8px. In the legacy account page that gap
 * is hidden because the fixed `Topnav` band (pinned at 72) sits in front of it;
 * the card-management hero removes that band, so the compact bar has to pin at
 * the real 72 to sit flush under the header instead of leaving an 8px strip of
 * scrolling content showing through.
 */
export const GLOBAL_HEADER_HEIGHT_PX = 72
